import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Calculate as CalculateIcon,
  PersonAdd as AssignIcon,
} from '@mui/icons-material';
import {
  usePricingGroups,
  useCreatePricingGroup,
  useUpdatePricingGroup,
  useDeletePricingGroup,
  usePricingMatrix,
  useAddPricingMatrixPrice,
  usePricingSurcharges,
  useAddPricingSurcharge,
  useCalculatePricingQuote,
  useAssignCustomerToPricingGroup,
} from '../../hooks/useBilling';
import { useCustomers } from '../../hooks/useCustomers';
import SmartDataGrid from '../common/SmartDataGrid';
import { handleApiError } from '../../utils/errorHandler';
import { getCurrencySymbol } from '../../utils/invoice-pdf-general-section-utilities';

/**
 * PricingGroupsPanel - Manages customer pricing groups with tiered pricing
 * Integrates with pricing groups API for CRUD operations
 */
const PricingGroupsPanel = () => {
  const { t, i18n } = useTranslation('business');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const formatCurrency = useCallback((amount, currency = 'EUR') => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${(amount || 0).toLocaleString(i18n?.language || 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [i18n?.language]);

  // State management
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [surchargeDialogOpen, setSurchargeDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  // Form states
  const [groupForm, setGroupForm] = useState({ name: '', description: '', tier: 'default' });
  const [priceForm, setPriceForm] = useState({ carrier_id: '', country_code: '', weight_threshold: '', base_price: '' });
  const [surchargeForm, setSurchargeForm] = useState({ name: '', postcode_pattern: '', amount: '' });
  const [quoteForm, setQuoteForm] = useState({ customer_id: '', carrier_id: '', country_code: '', weight: '', postcode: '' });
  const [assignForm, setAssignForm] = useState({ customer_id: '', group_id: '' });
  const [quoteResult, setQuoteResult] = useState(null);

  // Data fetching
  const { data: groupsData, isLoading: groupsLoading, refetch: refetchGroups } = usePricingGroups();
  const { data: matrixData, isLoading: matrixLoading } = usePricingMatrix(
    selectedGroup?.id ? { pricing_group_id: selectedGroup.id } : {}
  );
  const { data: surchargesData, isLoading: surchargesLoading } = usePricingSurcharges(
    selectedGroup?.id ? { pricing_group_id: selectedGroup.id } : {}
  );
  const { data: customersData } = useCustomers();

  // Mutations
  const createGroupMutation = useCreatePricingGroup();
  const updateGroupMutation = useUpdatePricingGroup();
  const deleteGroupMutation = useDeletePricingGroup();
  const addPriceMutation = useAddPricingMatrixPrice();
  const addSurchargeMutation = useAddPricingSurcharge();
  const calculateQuoteMutation = useCalculatePricingQuote();
  const assignCustomerMutation = useAssignCustomerToPricingGroup();

  // Memoized data
  const groups = useMemo(() => groupsData?.items || groupsData || [], [groupsData]);
  const matrix = useMemo(() => matrixData?.items || matrixData || [], [matrixData]);
  const surcharges = useMemo(() => surchargesData?.items || surchargesData || [], [surchargesData]);
  const customers = useMemo(() => customersData?.items || customersData || [], [customersData]);

  // Filtered groups based on search
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groups;
    const query = searchQuery.toLowerCase();
    return groups.filter(g => 
      g.name?.toLowerCase().includes(query) || 
      g.description?.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);

  // Tier color mapping
  const getTierColor = (tier) => {
    const colors = {
      default: 'default',
      premium: 'primary',
      enterprise: 'secondary',
      partner: 'success',
    };
    return colors[tier] || 'default';
  };

  // Handlers
  const handleCreateGroup = async () => {
    try {
      await createGroupMutation.mutateAsync(groupForm);
      enqueueSnackbar(t('business.pricingGroups.messages.createSuccess'), { variant: 'success' });
      setCreateDialogOpen(false);
      setGroupForm({ name: '', description: '', tier: 'default' });
      refetchGroups();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error creating pricing group:');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await updateGroupMutation.mutateAsync({ groupId: selectedGroup.id, data: groupForm });
      enqueueSnackbar(t('business.pricingGroups.messages.updateSuccess'), { variant: 'success' });
      setEditDialogOpen(false);
      refetchGroups();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error updating pricing group:');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm(t('business.pricingGroups.messages.deleteConfirm'))) {
      try {
        await deleteGroupMutation.mutateAsync(groupId);
        enqueueSnackbar(t('business.pricingGroups.messages.deleteSuccess'), { variant: 'success' });
        setSelectedGroup(null);
        refetchGroups();
      } catch (error) {
        handleApiError(error, enqueueSnackbar, t, 'Error deleting pricing group:');
      }
    }
  };

  const handleAddPrice = async () => {
    try {
      await addPriceMutation.mutateAsync({
        groupId: selectedGroup.id,
        data: {
          ...priceForm,
          weight_threshold: parseFloat(priceForm.weight_threshold),
          base_price: parseFloat(priceForm.base_price),
        },
      });
      enqueueSnackbar(t('business.pricingGroups.messages.priceAddedSuccess'), { variant: 'success' });
      setPriceDialogOpen(false);
      setPriceForm({ carrier_id: '', country_code: '', weight_threshold: '', base_price: '' });
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error adding price:');
    }
  };

  const handleAddSurcharge = async () => {
    try {
      await addSurchargeMutation.mutateAsync({
        groupId: selectedGroup.id,
        data: {
          ...surchargeForm,
          amount: parseFloat(surchargeForm.amount),
        },
      });
      enqueueSnackbar(t('business.pricingGroups.messages.surchargeAddedSuccess'), { variant: 'success' });
      setSurchargeDialogOpen(false);
      setSurchargeForm({ name: '', postcode_pattern: '', amount: '' });
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error adding surcharge:');
    }
  };

  const handleCalculateQuote = async () => {
    try {
      const result = await calculateQuoteMutation.mutateAsync({
        customerId: quoteForm.customer_id,
        carrierId: quoteForm.carrier_id,
        countryCode: quoteForm.country_code,
        weight: parseFloat(quoteForm.weight),
        postcode: quoteForm.postcode,
      });
      setQuoteResult(result);
      enqueueSnackbar(t('business.pricingGroups.messages.quoteCalculated'), { variant: 'success' });
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error calculating quote:');
    }
  };

  const handleAssignCustomer = async () => {
    try {
      await assignCustomerMutation.mutateAsync({
        customerId: assignForm.customer_id,
        groupId: assignForm.group_id,
      });
      enqueueSnackbar(t('business.pricingGroups.messages.assignSuccess'), { variant: 'success' });
      setAssignDialogOpen(false);
      setAssignForm({ customer_id: '', group_id: '' });
      refetchGroups();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error assigning customer:');
    }
  };

  const openEditDialog = (group) => {
    setSelectedGroup(group);
    setGroupForm({ name: group.name, description: group.description || '', tier: group.tier || 'default' });
    setEditDialogOpen(true);
  };

  // Column definitions
  const groupColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { 
      field: 'name', 
      headerName: t('business.pricingGroups.columns.name'), 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
      ),
    },
    { 
      field: 'description', 
      headerName: t('business.pricingGroups.columns.description'), 
      flex: 1.5,
    },
    { 
      field: 'tier', 
      headerName: t('business.pricingGroups.columns.tier'), 
      width: 130,
      renderCell: (params) => (
        <Chip 
          label={t(`business.pricingGroups.tiers.${params.value || 'default'}`)} 
          size="small" 
          color={getTierColor(params.value)}
        />
      ),
    },
    { 
      field: 'customer_count', 
      headerName: t('business.pricingGroups.columns.customers'), 
      width: 120,
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'actions',
      headerName: t('business.pricingGroups.columns.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('business.pricingGroups.editGroup')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('business.pricingGroups.deleteGroup')}>
            <IconButton 
              size="small" 
              color="error" 
              onClick={(e) => { e.stopPropagation(); handleDeleteGroup(params.row.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('business.pricingGroups.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('business.pricingGroups.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('business.pricingGroups.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Tooltip title={t('business.pricingGroups.quote.title')}>
            <Button variant="outlined" startIcon={<CalculateIcon />} onClick={() => setQuoteDialogOpen(true)}>
              {t('business.pricingGroups.quote.calculate')}
            </Button>
          </Tooltip>
          <Tooltip title={t('business.pricingGroups.assign.title')}>
            <Button variant="outlined" startIcon={<AssignIcon />} onClick={() => setAssignDialogOpen(true)}>
              {t('business.pricingGroups.assign.assignButton')}
            </Button>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchGroups()}>
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            {t('business.pricingGroups.newGroup')}
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        {/* Groups List */}
        <Box sx={{ width: selectedGroup ? '50%' : '100%', borderRight: selectedGroup ? 1 : 0, borderColor: 'divider' }}>
          <SmartDataGrid
            rows={filteredGroups}
            columns={groupColumns}
            loading={groupsLoading}
            onRowClick={(params) => setSelectedGroup(params.row)}
            getRowClassName={(params) => params.row.id === selectedGroup?.id ? 'Mui-selected' : ''}
          />
        </Box>

        {/* Detail Panel */}
        {selectedGroup && (
          <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{selectedGroup.name}</Typography>
                  <Chip 
                    label={t(`business.pricingGroups.tiers.${selectedGroup.tier || 'default'}`)} 
                    size="small" 
                    color={getTierColor(selectedGroup.tier)}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <IconButton onClick={() => setSelectedGroup(null)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              {selectedGroup.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedGroup.description}
                </Typography>
              )}
            </Box>

            <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={t('business.pricingGroups.matrix.title')} />
              <Tab label={t('business.pricingGroups.surcharges.title')} />
            </Tabs>

            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
              {detailTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">{t('business.pricingGroups.matrix.subtitle')}</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setPriceDialogOpen(true)}>
                      {t('business.pricingGroups.matrix.addPrice')}
                    </Button>
                  </Box>
                  {matrixLoading ? (
                    <CircularProgress />
                  ) : matrix.length === 0 ? (
                    <Alert severity="info">{t('business.pricingGroups.matrix.noData')}</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('business.pricingGroups.matrix.carrier')}</TableCell>
                            <TableCell>{t('business.pricingGroups.matrix.country')}</TableCell>
                            <TableCell>{t('business.pricingGroups.matrix.weightThreshold')}</TableCell>
                            <TableCell>{t('business.pricingGroups.matrix.basePrice')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {matrix.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.carrier_id}</TableCell>
                              <TableCell>{row.country_code}</TableCell>
                              <TableCell>{row.weight_threshold} kg</TableCell>
                              <TableCell>{formatCurrency(row.base_price, row.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {detailTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">{t('business.pricingGroups.surcharges.subtitle')}</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setSurchargeDialogOpen(true)}>
                      {t('business.pricingGroups.surcharges.addSurcharge')}
                    </Button>
                  </Box>
                  {surchargesLoading ? (
                    <CircularProgress />
                  ) : surcharges.length === 0 ? (
                    <Alert severity="info">{t('business.pricingGroups.surcharges.noData')}</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('business.pricingGroups.surcharges.name')}</TableCell>
                            <TableCell>{t('business.pricingGroups.surcharges.postcodePattern')}</TableCell>
                            <TableCell>{t('business.pricingGroups.surcharges.amount')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {surcharges.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.name}</TableCell>
                              <TableCell><code>{row.postcode_pattern}</code></TableCell>
                              <TableCell>{formatCurrency(row.amount, row.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.newGroup')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('business.pricingGroups.columns.name')}
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('business.pricingGroups.columns.description')}
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>{t('business.pricingGroups.columns.tier')}</InputLabel>
              <Select
                value={groupForm.tier}
                label={t('business.pricingGroups.columns.tier')}
                onChange={(e) => setGroupForm({ ...groupForm, tier: e.target.value })}
              >
                <MenuItem value="default">{t('business.pricingGroups.tiers.default')}</MenuItem>
                <MenuItem value="premium">{t('business.pricingGroups.tiers.premium')}</MenuItem>
                <MenuItem value="enterprise">{t('business.pricingGroups.tiers.enterprise')}</MenuItem>
                <MenuItem value="partner">{t('business.pricingGroups.tiers.partner')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={!groupForm.name || createGroupMutation.isPending}>
            {t('common.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.editGroup')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('business.pricingGroups.columns.name')}
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('business.pricingGroups.columns.description')}
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>{t('business.pricingGroups.columns.tier')}</InputLabel>
              <Select
                value={groupForm.tier}
                label={t('business.pricingGroups.columns.tier')}
                onChange={(e) => setGroupForm({ ...groupForm, tier: e.target.value })}
              >
                <MenuItem value="default">{t('business.pricingGroups.tiers.default')}</MenuItem>
                <MenuItem value="premium">{t('business.pricingGroups.tiers.premium')}</MenuItem>
                <MenuItem value="enterprise">{t('business.pricingGroups.tiers.enterprise')}</MenuItem>
                <MenuItem value="partner">{t('business.pricingGroups.tiers.partner')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateGroup} disabled={!groupForm.name || updateGroupMutation.isPending}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Price Dialog */}
      <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.matrix.addPrice')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('business.pricingGroups.matrix.carrier')}
              value={priceForm.carrier_id}
              onChange={(e) => setPriceForm({ ...priceForm, carrier_id: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('business.pricingGroups.matrix.country')}
              value={priceForm.country_code}
              onChange={(e) => setPriceForm({ ...priceForm, country_code: e.target.value })}
              fullWidth
              required
              placeholder="FR, DE, ES..."
            />
            <TextField
              label={t('business.pricingGroups.matrix.weightThreshold')}
              value={priceForm.weight_threshold}
              onChange={(e) => setPriceForm({ ...priceForm, weight_threshold: e.target.value })}
              fullWidth
              required
              type="number"
            />
            <TextField
              label={t('business.pricingGroups.matrix.basePrice')}
              value={priceForm.base_price}
              onChange={(e) => setPriceForm({ ...priceForm, base_price: e.target.value })}
              fullWidth
              required
              type="number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleAddPrice} disabled={addPriceMutation.isPending}>
            {t('common.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Surcharge Dialog */}
      <Dialog open={surchargeDialogOpen} onClose={() => setSurchargeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.surcharges.addSurcharge')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('business.pricingGroups.surcharges.name')}
              value={surchargeForm.name}
              onChange={(e) => setSurchargeForm({ ...surchargeForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('business.pricingGroups.surcharges.postcodePattern')}
              value={surchargeForm.postcode_pattern}
              onChange={(e) => setSurchargeForm({ ...surchargeForm, postcode_pattern: e.target.value })}
              fullWidth
              required
              placeholder="^97[1-6].*"
              helperText="Regex pattern for postcode matching"
            />
            <TextField
              label={t('business.pricingGroups.surcharges.amount')}
              value={surchargeForm.amount}
              onChange={(e) => setSurchargeForm({ ...surchargeForm, amount: e.target.value })}
              fullWidth
              required
              type="number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurchargeDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleAddSurcharge} disabled={addSurchargeMutation.isPending}>
            {t('common.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculate Quote Dialog */}
      <Dialog open={quoteDialogOpen} onClose={() => { setQuoteDialogOpen(false); setQuoteResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.quote.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('business.pricingGroups.quote.subtitle')}
          </Typography>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>{t('business.pricingGroups.quote.customer')}</InputLabel>
              <Select
                value={quoteForm.customer_id}
                label={t('business.pricingGroups.quote.customer')}
                onChange={(e) => setQuoteForm({ ...quoteForm, customer_id: e.target.value })}
              >
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('business.pricingGroups.quote.carrier')}
              value={quoteForm.carrier_id}
              onChange={(e) => setQuoteForm({ ...quoteForm, carrier_id: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('business.pricingGroups.quote.country')}
              value={quoteForm.country_code}
              onChange={(e) => setQuoteForm({ ...quoteForm, country_code: e.target.value })}
              fullWidth
              required
              placeholder="FR"
            />
            <TextField
              label={t('business.pricingGroups.quote.weight')}
              value={quoteForm.weight}
              onChange={(e) => setQuoteForm({ ...quoteForm, weight: e.target.value })}
              fullWidth
              required
              type="number"
            />
            <TextField
              label={t('business.pricingGroups.quote.postcode')}
              value={quoteForm.postcode}
              onChange={(e) => setQuoteForm({ ...quoteForm, postcode: e.target.value })}
              fullWidth
              placeholder="75001"
            />

            {quoteResult && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                <Typography variant="subtitle2" gutterBottom>{t('business.pricingGroups.quote.result')}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('business.pricingGroups.quote.basePrice')}</Typography>
                  <Typography variant="body2">{formatCurrency(quoteResult.base_price, quoteResult.currency)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('business.pricingGroups.quote.surchargeTotal')}</Typography>
                  <Typography variant="body2">{formatCurrency(quoteResult.surcharge_total, quoteResult.currency)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                  <Typography variant="subtitle2">{t('business.pricingGroups.quote.totalPrice')}</Typography>
                  <Typography variant="subtitle2" color="primary">{formatCurrency(quoteResult.total_price, quoteResult.currency)}</Typography>
                </Box>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQuoteDialogOpen(false); setQuoteResult(null); }}>{t('common.close', 'Close')}</Button>
          <Button variant="contained" onClick={handleCalculateQuote} disabled={calculateQuoteMutation.isPending}>
            {t('business.pricingGroups.quote.calculate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Customer Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('business.pricingGroups.assign.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('business.pricingGroups.assign.subtitle')}
          </Typography>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>{t('business.pricingGroups.assign.selectCustomer')}</InputLabel>
              <Select
                value={assignForm.customer_id}
                label={t('business.pricingGroups.assign.selectCustomer')}
                onChange={(e) => setAssignForm({ ...assignForm, customer_id: e.target.value })}
              >
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('business.pricingGroups.assign.selectGroup')}</InputLabel>
              <Select
                value={assignForm.group_id}
                label={t('business.pricingGroups.assign.selectGroup')}
                onChange={(e) => setAssignForm({ ...assignForm, group_id: e.target.value })}
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleAssignCustomer} 
            disabled={!assignForm.customer_id || !assignForm.group_id || assignCustomerMutation.isPending}
          >
            {t('business.pricingGroups.assign.assignButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(PricingGroupsPanel);
